/**
 * Cadastro manual de sobra (D21): comprimento × largura obrigatórios —
 * sem medida a sobra não entra (o Cut não consegue usar).
 *
 * Com a prop `sobra`, o mesmo painel EDITA (pedido do dono, 2026-09-10):
 * o material fica travado — sobra de outra chapa é outra sobra.
 *
 * Material por busca tolerante (11/09): "absuluto", "bali 6", "ebano" acham
 * a chapa; escolher a chapa preenche espessura e veio pelo cadastro, e a
 * pessoa só corrige se a sobra fugir do padrão.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiAlertCircle } from "react-icons/fi";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { criarSobra, editarSobra } from "../../../services/suprimentos";
import { nomeMaterial } from "../../../utils/nomeMaterial";
import ComboboxBusca from "../Solicitacoes/ComboboxBusca";
import {
  ErrorMsg, Field, Form, FormGrid, InlineHint, Input,
  PrimaryButton, SecondaryButton, Select, Textarea,
} from "../styles";

const FORM_ID = "form-sobra";

const VAZIO = {
  produto: "", comprimento_mm: "", largura_mm: "", espessura_mm: "", tem_veio: "", box: "", observacao: "",
};

/** "400.0" -> "400"; "0.45" fica "0.45"; vazio continua vazio. */
function mm(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "";
}

function veioDe(v) {
  return v === null || v === undefined ? "" : String(v);
}

function formDe(sobra) {
  if (!sobra) return VAZIO;
  return {
    produto: sobra.produto || "",
    comprimento_mm: mm(sobra.comprimento_mm),
    largura_mm: mm(sobra.largura_mm),
    espessura_mm: mm(sobra.espessura_mm),
    tem_veio: veioDe(sobra.tem_veio),
    box: sobra.box || "",
    observacao: sobra.observacao || "",
  };
}

export default function NovaSobraPanel({ open, onClose, produtos, boxes, onCriada, sobra = null }) {
  const { request } = useAuth();
  const editando = Boolean(sobra);
  const [form, setForm] = useState(() => formDe(sobra));
  const [erro, setErro] = useState(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Reabrir para outra sobra (ou voltar a "nova") recarrega o formulário.
  useEffect(() => { if (open) { setForm(formDe(sobra)); setErro(null); } }, [open, sobra]);

  const opcoesMateriais = useMemo(
    () => produtos.map((p) => ({ id: p.id, label: nomeMaterial(p), sublabel: p.codigo, codigo: p.codigo, produto: p })),
    [produtos],
  );
  const produto = produtos.find((p) => p.id === form.produto);
  const materialSelecionado = opcoesMateriais.find((o) => o.id === form.produto) || null;
  const medidaTravada = editando && sobra.estado === "reservada";

  // Escolher a chapa traz espessura e veio do cadastro (item 3 do pedido).
  const escolherMaterial = (opcao) => {
    const p = opcao?.produto;
    setForm((prev) => ({
      ...prev,
      produto: p ? p.id : "",
      espessura_mm: p ? mm(p.espessura_mm) : "",
      tem_veio: p ? veioDe(p.tem_veio) : "",
    }));
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.produto || !form.comprimento_mm || !form.largura_mm) {
      setErro("Material, comprimento e largura são obrigatórios.");
      return;
    }
    setErro(null);
    const dados = {
      comprimento_mm: form.comprimento_mm,
      largura_mm: form.largura_mm,
      espessura_mm: form.espessura_mm || null,
      tem_veio: form.tem_veio === "" ? null : form.tem_veio === "true",
      box: form.box || null,
      observacao: form.observacao || "",
    };
    try {
      if (editando) {
        await editarSobra(request, sobra.id, dados);
      } else {
        await criarSobra(request, { produto: form.produto, ...dados });
      }
      setForm(VAZIO);
      await onCriada();
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || (editando ? "Erro ao salvar sobra." : "Erro ao cadastrar sobra."));
    }
  };

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={editando ? `Editar ${sobra.codigo}` : "Nova sobra"}
      width="min(90vw, 480px)"
      // Rodapé fixo do painel (auditoria mobile 2026-09-24): botões fora do
      // <form>, ligados pelo atributo `form`.
      footer={(
        <>
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" form={FORM_ID}>{editando ? "Salvar alterações" : "Cadastrar sobra"}</PrimaryButton>
        </>
      )}
    >
      <Form id={FORM_ID} onSubmit={salvar}>
        <Field>
          Material (chapa) *
          {editando ? (
            <Input value={nomeMaterial({ nome: sobra.produto_nome, espessura_mm: produto?.espessura_mm || sobra.espessura_mm })} disabled readOnly />
          ) : (
            <ComboboxBusca
              inputId="sobra-material"
              opcoes={opcoesMateriais}
              valor={materialSelecionado}
              onSelecionar={escolherMaterial}
              placeholder="Busque pela cor: absoluto, bali 6, branco tx…"
              carregando={produtos.length === 0}
            />
          )}
          {editando ? <InlineHint>O material não muda: sobra de outra chapa é outra sobra.</InlineHint> : null}
          {!editando && produto ? (
            <InlineHint>
              Espessura e veio vieram do cadastro de {nomeMaterial(produto)}; ajuste só se a sobra for diferente.
            </InlineHint>
          ) : null}
        </Field>
        <FormGrid>
          <Field>
            Comprimento (mm) *
            <Input type="number" step="0.1" min="1" value={form.comprimento_mm} onChange={set("comprimento_mm")} required disabled={medidaTravada} />
          </Field>
          <Field>
            Largura (mm) *
            <Input type="number" step="0.1" min="1" value={form.largura_mm} onChange={set("largura_mm")} required disabled={medidaTravada} />
          </Field>
        </FormGrid>
        {medidaTravada ? (
          <InlineHint>Reservada por {sobra.reservada_por_ref || "um plano de corte"}: libere a reserva para mudar a medida.</InlineHint>
        ) : null}
        <FormGrid>
          <Field>
            Espessura (mm)
            <Input type="number" step="0.1" min="0" value={form.espessura_mm} onChange={set("espessura_mm")} placeholder={mm(produto?.espessura_mm)} disabled={medidaTravada} />
          </Field>
          <Field>
            Veio
            <Select value={form.tem_veio} onChange={set("tem_veio")}>
              <option value="">Como o material</option>
              <option value="true">Com veio</option>
              <option value="false">Sem veio</option>
            </Select>
          </Field>
        </FormGrid>
        <FormGrid>
          <Field>
            Box
            <Select value={form.box} onChange={set("box")}>
              <option value="">— Sem box —</option>
              {boxes.filter((b) => b.ativo).map((b) => <option key={b.id} value={b.id}>{b.codigo}{b.nome ? ` — ${b.nome}` : ""}</option>)}
            </Select>
          </Field>
          <span />
        </FormGrid>
        <Field>
          Observação
          <Textarea rows={2} maxLength={300} value={form.observacao} onChange={set("observacao")} />
        </Field>
        {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
      </Form>
    </SlideOverPanel>
  );
}
